import React, { useEffect, useRef } from 'react';
import { format, isToday, isTomorrow } from 'date-fns';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Message from 'components/molecules/smsDashboard/message';
import ConversationEmptyIcon from 'components/atoms/icons/conversationEmptyIcon';
import { DateFormats } from 'infrastructure/enums';
import { useScheduledMessages } from 'infrastructure/hooks/smsDashboard/useScheduledMessages';

import s from './styles.module.scss';

import type {
  IConversation,
  ConversationType,
  IScheduledMessagesListRes,
  IScheduledMessage,
} from 'infrastructure/interfaces';
import type { RootState } from 'store';

interface IMessagesScheduledListProps {
  currentConversation: IConversation;
  isScheduledMessagesShown: boolean;
}

const messagesWrapperClass = s['messages-wrapper'];
export const scrollMessagesToBottom = () => {
  const el = document.querySelector(`.${messagesWrapperClass}`);
  if (!el) return;
  el.scrollTop = el.scrollHeight;
};

const MessagesScheduledList: React.FC<IMessagesScheduledListProps> = (
  props,
) => {
  const { currentConversation, isScheduledMessagesShown } = props;
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const { conversationType } = useSelector(
    (state: RootState) => state.smsDashboard,
  );

  useEffect(() => {
    scrollMessagesToBottom();
  }, [currentConversation]);

  const { scheduledMessages, scheduledMessagesLoading } = useScheduledMessages({
    conversationId: currentConversation.id,
    enabled: isScheduledMessagesShown,
  });

  const groupByDate = (data: IScheduledMessagesListRes) => {
    const dataByDate: { [key: string]: any } = {};

    data?.items?.forEach((item: IScheduledMessage) => {
      const messageDate = new Date(item.scheduledTime);
      const formattedDate = format(messageDate, DateFormats['yyyy-MM-dd']);

      if (!dataByDate[formattedDate]) {
        dataByDate[formattedDate] = [];
      }

      dataByDate[formattedDate].push(item);
    });

    return Object.fromEntries(
      Object.entries(dataByDate).sort(([a], [b]) => b.localeCompare(a)),
    );
  };

  const groupedByDate = scheduledMessages
    ? Object.entries(groupByDate(scheduledMessages))
    : [];

  const formatDate = (dateToFormat: Date | string) => {
    const date = new Date(dateToFormat);
    if (isToday(date)) {
      return `${t('smsDashboard.scheduled_for')} ${t('smsDashboard.today')}`;
    }
    if (isTomorrow(date)) {
      return `${t('smsDashboard.scheduled_for')} ${t('smsDashboard.tomorrow')}`;
    }
    return format(date, DateFormats['dd MMMM, yy']);
  };

  const messageGroupDateTitle = (date: string) =>
    format(new Date(date), DateFormats['MMMM dd, yyyy']);

  return (
    <div className={messagesWrapperClass} ref={containerRef}>
      {groupedByDate.map((group) => (
        <div className={s['message-group']} key={group[0]} id={group[0]}>
          <div
            className={s['message-group-date']}
            title={messageGroupDateTitle(group[0])}
          >
            {formatDate(group[0])}
          </div>
          {(group[1] as IScheduledMessage[])
            .reverse()
            .filter((message: IScheduledMessage, index: number, self: any) => {
              return (
                self.findIndex(
                  (otherMessage: IScheduledMessage) =>
                    otherMessage.id === message.id,
                ) === index
              );
            })
            .map((message: IScheduledMessage, index: number) => {
              const {
                body,
                authorType,
                status,
                from,
                to,
                action,
                author,
                mediaUrl,
                readBy,
                readAt,
                scheduledTime,
              } = message;
              const authorName =
                authorType === 'patient'
                  ? currentConversation?.patient?.fullNameWithOnlyTitle
                  : author?.fullNameWithProfession;
              return (
                <Message
                  left={false}
                  body={body}
                  action={action}
                  authorType={authorType}
                  authorName={authorName}
                  status={status}
                  createdAt={new Date(scheduledTime)}
                  to={to}
                  from={from}
                  conversationType={conversationType as ConversationType}
                  key={index}
                  mediaUrl={mediaUrl}
                  readBy={readBy}
                  readAt={readAt}
                  handleJumpToComment={() => {}}
                  isShowJumpToMsgBtn={false}
                  searchValue=""
                />
              );
            })}
        </div>
      ))}
      {!groupedByDate?.length && scheduledMessagesLoading === false && (
        <div className={s['empty-conversation']} style={{ height: '100%' }}>
          <div className={s['message-wrapper']}>
            <div className={s['empty-message']}>
              <ConversationEmptyIcon />
              <div className={s.message}>
                {t('smsDashboard.scheduled_messages_are_empty')}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesScheduledList;
